import { json, type MetaFunction, LoaderFunctionArgs } from "@remix-run/node";
import { useLoaderData } from "@remix-run/react";
import { Layout } from "~/components/layout";
import { MarkdownContent } from "~/components/markdown-content";
import { ArticleHeader } from "~/components/article-header";
import { ArticleFooter } from "~/components/article-footer";
import { getArticleBySlug } from "~/.server/content.server";
import { NotFoundErrorBoundary } from "~/components/error-boundary";

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  if (!data?.article) {
    return [
      { title: "Article Not Found - Weather Route Support" },
      { name: "description", content: "The requested article could not be found" },
    ];
  }

  return [
    { title: `${data.article.title} - Weather Route Support` },
    { name: "description", content: data.article.summary },
    { property: "og:title", content: data.article.title },
    { property: "og:description", content: data.article.summary },
    { property: "og:type", content: "article" },
    { name: "twitter:card", content: "summary" },
    { name: "twitter:title", content: data.article.title },
    { name: "twitter:description", content: data.article.summary },
  ];
};

export async function loader({ params }: LoaderFunctionArgs) {
  const { slug } = params;
  
  if (!slug) {
    throw new Response("Not Found", { status: 404 });
  }
  
  const article = await getArticleBySlug(slug);
  
  if (!article) {
    throw new Response("Not Found", { status: 404 });
  }
  
  return json({ article });
}

export function ErrorBoundary() {
  return <NotFoundErrorBoundary />;
}

export default function ArticleDetail() {
  const { article } = useLoaderData<typeof loader>();
  
  return (
    <Layout>
      <article className="py-8">
        <div className="container px-4 mx-auto">
          <div className="max-w-3xl mx-auto">
            <ArticleHeader article={article} />
            
            <div className="border-t pt-8">
              <MarkdownContent content={article.content} className="mb-12" />
            </div>

            <ArticleFooter />
          </div>
        </div>
      </article>
    </Layout>
  );
} 